import {
	FxGamesLibWebPublicDataBase,
	FxIdDomainPushNotificationsTokensPushNotificationPlatform,
	FxIdDomainStoreEnumsSupportedWebPublishingPlatform,
	FxIdWebFeaturesAuthAuthenticateFromSocialRequest,
	FxIdWebFeaturesAuthAuthenticateFromSocialResponse,
	FxIdWebFeaturesFriendsGetFriendsResponse,
	FxIdWebFeaturesStoreGetPaymentStatusResponse
} from "../Api/gen";
import OpenApiClient from "../Api/OpenApiClient";
import { awaitPaymentSettled } from "./awaitPaymentSettled";
import { OS } from "@mantine/hooks/lib/use-os/use-os";
import { findAdapterConfig } from "@app/Config";
import { IframeType, IframeTypeToSupportedPlatformMap } from "@app/Screens/WebPlayer/Hooks";
import { getDocUrl } from "@app/Utils/Functions";
import { exitFullscreen } from "@app/Utils/Utils";
import { openIframeModal } from "@app/Components/Modals";
import * as Analytics from "../Utils/Analytics";
export interface IFxIdSdkAdapterOpenModalRequestDto {
	Url: string;
}

export interface IFxIdSdkAdapterOpenModalResultDto {
	Success: boolean;
}

export interface IFxIdSdkAdapterPushTokenResponseDto {
	Token?: string;
}

export type IFxIdSdkAdapterEmptyDto = unknown;

export enum FxIdSdkAdapterStickyBannerShowErrorReason {
	Unknown = 0,
	AdsNotConnected = 1,
	NotImplemented = 999
}

export interface IFxIdSdkSaveToClipboardRequest {
	/**
	 * Текст для буфера обмена
	 */
	data: string;
	/**
	 * Если буфер обмена не доступен, то если тут стоит true будет отображена модалка из которой пользователь
	 * сможет скопировать данные сам
	 */
	showModalOnFail?: boolean;
}

export interface IFxIdSdkSaveToClipboardResponse {
	/**
	 * Удалось ли скопировать данные в буфер с помощью апи браузера
	 */
	savedToClipboard: boolean;
}

export interface IFxIdSdkAdapterStickyBannerShowResultDto {
	success: boolean;
	errorReason?: FxIdSdkAdapterStickyBannerShowErrorReason;
}

export interface IFxIdSdkGetUrlHashDto {
	hash: string;
}

export interface IFxIdSdkSetUrlHashDto {
	hash: string;
}

export interface IFxIdSdkShowLinkShareModalDto {
	Link: string;
}

export interface IFxIdSdkSetStatusDto {
	name: string;
	status: string;
	priority: number;
}

export interface ISafeArea {
	left: number;
	right: number;
	top: number;
	bottom: number;
}

export interface IFxIdSdkAdapterInfo {
	game: string;
	environment: {
		os: OS;
		isMobile: boolean;
		safeArea: ISafeArea;
	};
	fxId: {
		userId: string;
		marketingEnabled: boolean;
		locale: string;
		geoCountry: string;
		token: string;
		gameToken: string;
		gameUid: string;
		guest: boolean;
		hasEmail: boolean;
		emailValidated: boolean;
		hasCompletedOrder: boolean;
	};
	social: IFxIdSdkAdapterInfoSocial;
}

export enum EFxIdSdkAdapterInfoSocialCapability {
	/**
	 * Поддерживается список друзей
	 */
	FriendsList = 0,
	/**
	 * Поддерживается приглашение друзей
	 */
	FriendsInvite = 1,
	/**
	 * Необходимо отключать игры с случайным исходом (рулетка итд)
	 */
	GamesOfChanceDisabled = 2,
	/**
	 * Можно ли показывать дилог с предложением поставить оценку
	 */
	RateApplication = 3,
	/**
	 * Нельзя указывать ссылки на другие платформы
	 */
	OutgoingLinksForbidden = 4,
	/**
	 * Доступна ли реклама на платформе
	 */
	AdsAvailable = 5,
	// Значения 6–8 заняты на master (LegalDialogsDisabled, SupportDialogsDisabled, ExternalIncentivesDisabled),
	// здесь не используются.
	/** Площадка задаёт имя игрока сама: игра не должна предлагать собственное переименование */
	InGameNameChangeDisabled = 9
}

export type TSocialFlavor = "vkcom-direct";

/** Окружение SDK CrazyGames: SDK.environment */
export type CrazyGamesEnvironment = "local" | "crazygames" | "disabled";

/** Пользователь CrazyGames: результат SDK.user.getUser() */
export interface CrazyGamesUser {
	__dangerousUserId: string;
	username: string;
	profilePictureUrl: string;
}

/** Настройки игры CrazyGames: SDK.game.settings */
export interface CrazyGamesGameSettings {
	muteAudio: boolean;
	disableChat: boolean;
}

/** Информация о системе игрока CrazyGames: SDK.user.systemInfo */
export interface CrazyGamesSystemInfo {
	countryCode: string;
	locale: string;
	/** Известные device.type: desktop | tablet | mobile; string допускает новые значения CrazyGames */
	device: { type: string };
	os: { name: string; version: string };
	browser: { name: string; version: string };
	/** Известные значения: google_play_store | apple_store | pwa | web; string допускает новые значения CrazyGames */
	applicationType: string;
}

/** Секция CrazyGames: данные SDK CrazyGames v3 в форме CrazyGames */
export interface CrazyGamesSocialInfo {
	/** Результат SDK.user.getUser(); null — гость площадки */
	user: CrazyGamesUser | null;
	settings: CrazyGamesGameSettings;
	environment: CrazyGamesEnvironment;
	isUserAccountAvailable: boolean;
	systemInfo: CrazyGamesSystemInfo;
}

/** Общие данные площадки, соответствующие контракту master. */
export type SocialInfo = IFxIdSdkAdapterInfoSocial;

/** Секция CrazyGames в контракте legacy SDK. */
export type IFxIdSdkAdapterInfoSocialCrazyGames = CrazyGamesSocialInfo;

export interface IFxIdSdkAdapterInfoSocial {
	/**
	 * Признак социальной сети
	 */
	social: FxIdDomainStoreEnumsSupportedWebPublishingPlatform;
	/**
	 * Дополнительный признак для социльной сети
	 */
	socialFlavor?: TSocialFlavor;
	/**
	 * Идентификатор из социальной сети
	 */
	userId: string;
	/**
	 * Доступны ли платежи на платформе
	 */
	paymentsAvailable: boolean;
	/**
	 * Поддерживаемые возможности
	 */
	capabilities: EFxIdSdkAdapterInfoSocialCapability[];

	/**
	 * Опциональные данные - доступны не везде и не на всех платформах
	 */
	displayName?: string;
	firstName?: string;
	lastName?: string;
	gender?: EFxIdSdkGender;
	/**
	 * Некоторые платформы предоставляют данные о размере аватар, некоторые нет.
	 * Некоторые предоставляют дополнительную информацию о аватаре, некоторые нет
	 */
	photo?: {
		width?: number;
		height?: number;
		/**
		 * Ссылка на фотографию/аватар
		 */
		url: string;
	};
	/**
	 * Некоторые платформы позволяют установить свой язык пользователя. Вы должны реагировать на ChangeLanguage
	 */
	socialLocale?: string;
	/**
	 * Управление звуком со стороны площадки.
	 * true — площадка требует, чтобы игра молчала; false — площадка разрешает звук;
	 * поля нет — площадка звуком игры не управляет
	 */
	audioMuted?: boolean;
	/**
	 * Данные по соацильной сети VkontakteCom
	 */
	vkcom?: IFxIdSdkAdapterInfoSocialVkCom;
	/** Секция CrazyGames; есть только на CrazyGames */
	crazyGames?: CrazyGamesSocialInfo;
}

export interface IFxIdSdkAdapterInfoSocialVkCom {
	appId: number;
	defaultApiVersion: string;
	defaultAccessToken: string;
}

export type BuyProductStatus = "completed" | "cancelled" | "pending";

export interface IFxIdSdkAdapterBuyProductRequest {
	/**
	 * SKU товара для покупки
	 */
	sku: string;
	/**
	 * Произвольная строчка которая будет передана на сервер партнера после акцепта платежа
	 */
	developerPayload?: string;
	/**
	 * Название покупаемого товара.
	 * По возможности сервер возьмет эту строчку если ее надо будет показаться пользователю во время платежа
	 */
	productNameHint?: string;
	/**
	 * Описание покупаемого товара.
	 * По возможности сервер возьмет эту строчку если ее надо будет показаться пользователю во время платежа
	 */
	productDescriptionHint?: string;
	awaitPayment?: boolean;
}

export interface IFxIdSdkAdapterStatInitializeRequest {
	userId: string;
	userLevel: number;
	buildVersion: string;
	trackingEnabled?: boolean;
}

export interface IFxIdSdkAdapterStatEventRequest {
	name: string;
	type: string;
	subtype: string;
	section: string;
	value: number;
	data?: string;
}

export interface IFxIdSdkAdapterChangeLanguageRequest {
	lang: string;
}

export interface IFxIdSendVkBridgeRequest {
	method: string;
	props: any;
}

export interface IFxIdSendCrazyGamesSdkRequest {
	method: string;
	props?: any;
}

export interface IFxIdSdkAdapterBuyProductResponse {
	error?: string;
	transactionId?: string;
	errorCode?: EFxIdSdkAdapterBuyProductResponseErrorCode;
	stats?: {
		currency: string;
		currencyAmount: number;
		usdAmount: number;
	};
	status?: BuyProductStatus;
}

export enum EFxIdSdkAdapterBuyProductResponseErrorCode {
	Unknown = 0,
	UserCancelled = 1
}

export const FxIdSdkAdapterSocialSettingDefault: IFxIdSdkAdapterSocialSettings = {
	ui: {
		disabled: true
	}
};

export interface IFxIdSdkAdapterSocialSettings {
	ui: {
		// Если тру, то не показываем оформление совсем
		disabled: boolean;
	};
	i18n?: {
		gameName?: string | null;
	};
}

export interface IFxIdSdkAuthenticateApiResult {
	authResponse: FxIdWebFeaturesAuthAuthenticateFromSocialResponse;
	changeEvent?: IFxIdSdkOnAccountChangedEvent;
	changeInfoEvent?: IFxIdSdkOnChangeUserInfoEvent;
}

export interface IFxIdSdkOnAccountChangedEvent {
	oldAccountId: number;
	newAccountId: number;
}

export enum EFxIdSdkFocusChangedReason {
	Hint = "hint",
	Pip = "pip",
	Other = "other"
}

export interface IFxIdSdkRatingResponse {
	isRatingAvailable: boolean;
	rating?: number;
	created?: Date;
	comment?: string;
	platform?: string;
	success?: boolean;
}

export interface IFxIdSdkOnFocusChangedEvent {
	focus: boolean;
	reason?: EFxIdSdkFocusChangedReason;
	hint?: string;
}

export interface IFxIdSdkOnChangeUserInfoEvent {
	newInfo: IFxIdSdkAdapterInfo;
}

/** Данные площадки (social) изменились посреди сессии */
export interface SocialInfoChangedEvent {
	social: SocialInfo;
}

/** Событие изменения данных площадки в контракте legacy SDK. */
export type IFxIdSdkOnChangeSocialInfoEvent = SocialInfoChangedEvent;

export interface IFxIdSdkOnChangeLanguageEvent {
	language: string;
}

export interface IFxIdSdkAuthenticateUser {
	preferredSocials?: string[];
	force?: {
		providerId: string;
	};
	import?: {
		socialIdentifier: string;
		socialIdentifierType: string;
	};
}

export enum EFxIdSdkGender {
	Unspecified = 0,
	Female,
	Male = 2
}

export interface IFxIdSdkGetFriendsResultFriend {
	firstName?: string;
	lastName?: string;
	displayName?: string;
	uid: string;
	gender?: EFxIdSdkGender;
	photo?: {
		width?: number;
		height?: number;
		url: string;
	};
}

export type IFxIdSdkAdapterGetFriendsRequest = unknown;

export interface IFxIdSdkAdapterInviteFriendsRequest {
	inviteText: string;
	base64image?: string;
	trackString?: string;
	gif?: { url: string };
	video?: { url: string };
}

export type IFxIdSdkAdapterAuthenticateUserRequest = unknown;

export type IFxIdSdkAdapterAuthenticateUserResponse = unknown;

export interface IFxIdSdkGetFriendsResult {
	friends: IFxIdSdkGetFriendsResultFriend[];
}

export interface IFxIdSdkInviteFriendsResult {
	friends: IFxIdSdkGetFriendsResultFriend[];
}

export interface IFxIdSdkGetFriendsResultForSdk extends FxIdWebFeaturesFriendsGetFriendsResponse {
	socialFriends: IFxIdSdkGetFriendsResultFriend[];
}

export interface IExportedFxIdSdkMethod {
	AuthenticateUser(requestJson: string): Promise<void>;

	AuthenticateApi(request: FxIdWebFeaturesAuthAuthenticateFromSocialRequest): Promise<IFxIdSdkAuthenticateApiResult>;

	IsAuthenticationApiInProgress(): boolean;

	DispatchAdsFinished(): void;

	DispatchAdsSkipped(): void;

	DispatchAdsFailed(): void;

	DispatchFocusChanged(data: IFxIdSdkOnFocusChangedEvent): void;

	/**
	 * Адаптер сообщает, что данные площадки могли измениться; SDK перечитает social
	 * и отправит событие, только если оно изменилось
	 */
	DispatchSocialInfoChanged(): void;
}

export interface IFxIdJoinCommunityRequest {
	community_id: string;
}

export interface IFxIdJoinCommunityResult {
	success: boolean;
}

export type IFxIdAddToFavoritesRequest = unknown;

export interface IFxIdAddToFavoritesResult {
	success: boolean;
}

export interface IFxIdSdkWordsFilterRequest {
	text: string;
}

export interface IFxIdSdkWordsFilterResult {
	success: boolean;
}

export type IFxIdSdkRateApplicationRequest = unknown;

export interface IFxIdSdkRateApplicationResult {
	success: boolean;
}

export type IFxIdSdkSendFxEventResult = unknown;

export interface IFxIdSdkAdapterConsentManagerStatus {
	consentExists: boolean;
	consentData: any;
	cmpData: any;
}

export interface IFxIdSdkAdapterCanReviewResult {
	canReview: boolean;
}

export interface IFxIdSdkAdapterRequestReviewResult {
	shown: boolean;
}

export abstract class FxIdSdkBaseAdapter {
	abstract readonly adapter: IframeType;
	readonly defaultSocialSettings: IFxIdSdkAdapterSocialSettings = FxIdSdkAdapterSocialSettingDefault;

	protected constructor(
		protected exportedSdk: IExportedFxIdSdkMethod,
		protected game: string,
		protected config: FxGamesLibWebPublicDataBase
	) {}

	abstract Initialize(): Promise<void>;

	abstract GetSocialInfo(): Promise<IFxIdSdkAdapterInfoSocial>;

	abstract BuyProduct(request: IFxIdSdkAdapterBuyProductRequest): Promise<IFxIdSdkAdapterBuyProductResponse>;

	GetPaymentStatusInfo(transactionId: string): Promise<FxIdWebFeaturesStoreGetPaymentStatusResponse> {
		return OpenApiClient.Store.fxIdWebFeaturesStoreGetPaymentStatusEndpoint({
			PaymentId: transactionId
		});
	}

	async BuyProductWithAwait(
		request: IFxIdSdkAdapterBuyProductRequest,
		signal?: AbortSignal
	): Promise<IFxIdSdkAdapterBuyProductResponse> {
		const result = await this.BuyProduct(request);
		if (request.awaitPayment !== true || result.transactionId == null || result.error != null) {
			return result;
		}

		const settled = await awaitPaymentSettled({
			fetchStatus: async () => await this.GetPaymentStatusInfo(result.transactionId!),
			signal
		});
		return {
			...result,
			status:
				settled.Status === 2 || settled.Status === 999
					? "completed"
					: settled.Status === 898 || settled.Status === 899
						? "cancelled"
						: "pending"
		};
	}

	async SocialSettings(): Promise<IFxIdSdkAdapterSocialSettings> {
		const adapterSettings = findAdapterConfig(this.config.PublicWebClientConfig.Adapters, this.adapter);
		const adapterUiDisabled = adapterSettings?.DisableHeaderUi === true;

		const settings: IFxIdSdkAdapterSocialSettings = {
			...this.defaultSocialSettings,
			ui: {
				...this.defaultSocialSettings.ui,
				disabled: adapterUiDisabled
			},
			i18n: {
				gameName: adapterSettings?.I18n?.GameName
			}
		};

		if (adapterUiDisabled) {
			settings.ui.disabled = true;
		}

		return Promise.resolve(settings);
	}

	abstract RegisterShareHandlers(): Promise<void>;

	OnAfterLogin(): Promise<void> {
		return Promise.resolve();
	}

	abstract StoreCurrency(): Promise<string | undefined>;

	abstract StatInitialize(request: IFxIdSdkAdapterStatInitializeRequest): Promise<void>;

	abstract StatEvent(request: IFxIdSdkAdapterStatEventRequest): Promise<void>;

	CanReview(): Promise<IFxIdSdkAdapterCanReviewResult> {
		return Promise.resolve({ canReview: false });
	}

	RequestReview(): Promise<IFxIdSdkAdapterRequestReviewResult> {
		return Promise.resolve({ shown: false });
	}

	ReviewInfo(): Promise<IFxIdSdkRatingResponse> {
		return Promise.resolve({ isRatingAvailable: false });
	}

	StoreAdditionalData(): Promise<unknown | undefined> {
		return Promise.resolve(undefined);
	}

	AdsIsVideoReady(): Promise<boolean> {
		// ignore
		return Promise.resolve(false);
	}

	AdsShowVideo(): Promise<void> {
		// ignore
		return Promise.resolve();
	}

	AdsStickyBannerShow(): Promise<IFxIdSdkAdapterStickyBannerShowResultDto> {
		return Promise.resolve({
			success: false,
			errorReason: FxIdSdkAdapterStickyBannerShowErrorReason.NotImplemented
		});
	}

	AdsIsInterstitialReady(): Promise<boolean> {
		// ignore
		return Promise.resolve(false);
	}

	AdsShowInterstitial(): Promise<void> {
		// ignore
		return Promise.resolve();
	}

	AdsStickyBannerHide(): Promise<IFxIdSdkAdapterStickyBannerShowResultDto> {
		return Promise.resolve({
			success: false,
			errorReason: FxIdSdkAdapterStickyBannerShowErrorReason.NotImplemented
		});
	}

	GetFriends(_request: IFxIdSdkAdapterGetFriendsRequest): Promise<IFxIdSdkGetFriendsResult> {
		return Promise.resolve({ friends: [] });
	}

	InviteFriends(_request: IFxIdSdkAdapterInviteFriendsRequest): Promise<IFxIdSdkInviteFriendsResult> {
		return Promise.resolve({ friends: [] });
	}

	AuthenticateUser(
		_request: IFxIdSdkAdapterAuthenticateUserRequest
	): Promise<IFxIdSdkAdapterAuthenticateUserResponse> {
		return Promise.resolve({});
	}

	JoinCommunity(_request: IFxIdJoinCommunityRequest): Promise<IFxIdJoinCommunityResult> {
		return Promise.resolve({ success: false });
	}

	AddToFavorites(_request: IFxIdAddToFavoritesRequest): Promise<IFxIdAddToFavoritesResult> {
		return Promise.resolve({ success: false });
	}

	WordsFilter(request: IFxIdSdkWordsFilterRequest): Promise<IFxIdSdkWordsFilterResult> {
		return Promise.resolve({ success: true });
	}

	RateApplication(request: IFxIdSdkRateApplicationRequest): Promise<IFxIdSdkRateApplicationResult> {
		return Promise.resolve({ success: true });
	}

	IsFocusChanged() {
		const eventHandler = () => {
			// Проверяем, скрыта ли страница
			if (document.hidden) {
				this.exportedSdk.DispatchFocusChanged({ focus: false });
			} else {
				this.exportedSdk.DispatchFocusChanged({ focus: true });
			}
		};
		document.addEventListener("visibilitychange", eventHandler);
	}

	async LoadingReady(): Promise<void> {
		return Promise.resolve();
	}

	async GameStarted(): Promise<void> {
		return Promise.resolve();
	}

	async GameStopped(): Promise<void> {
		return Promise.resolve();
	}

	/**
	 * Перенаправляет некоторые URL-адреса на внутренние ресурсы или специальные страницы.
	 * Это временное решение для обработки специфичных ссылок.
	 * @param url - исходный URL для возможного перенаправления.
	 * @returns измененный или оригинальный URL.
	 */
	protected async __HACK_RemapUrl(url: URL): Promise<URL> {
		if (import.meta.env.VITE_APP_TENANT_OVERRIDE === "" || import.meta.env.VITE_APP_TENANT_OVERRIDE === "FX") {
			if (
				url.toString().startsWith("https://fxgames.zendesk.com/hc") ||
				url.toString().match(/\/docs\/(.*)\/faq.html/)
			) {
				// log.info("Remapping to freshdesk");
				// return new URL("https://fxgames.freshdesk.com/support/solutions");
				log.info("Remapping to faq.txt");
				const docUrl = await getDocUrl({
					docFile: "faq.html",
					adapter: this.adapter
				});
				return docUrl;
			}
			if (url.toString().startsWith("https://discord")) {
				log.info("Remapping discrod url");
				const url = new URL(`${import.meta.env.BASE_URL}/discord-qr`, window.location.origin);
				url.searchParams.append("game", this.game);
				return url;
			}
			if (url.toString().startsWith("https://fx.gl/terms-of-service")) {
				log.info("Remapping tos url");
				const docUrl = await getDocUrl({
					docFile: "tos.txt",
					adapter: this.adapter
				});
				return docUrl;
			}
			if (url.toString().startsWith("https://fx.gl/privacy-policy")) {
				log.info("Remapping pp url");
				const docUrl = await getDocUrl({
					docFile: "pp.txt",
					adapter: this.adapter
				});
				return docUrl;
			}
		}
		return url;
	}

	async OpenModal(requestJson: string): Promise<IFxIdSdkAdapterOpenModalResultDto> {
		log.info("Opening modal %o", requestJson);

		await exitFullscreen();

		const request: IFxIdSdkAdapterOpenModalRequestDto = JSON.parse(requestJson);
		const url = await this.__HACK_RemapUrl(new URL(request.Url));

		if (this.adapter !== "fxid") {
			const platform = IframeTypeToSupportedPlatformMap[this.adapter];
			url.searchParams.append("social", FxIdDomainStoreEnumsSupportedWebPublishingPlatform[platform]);
		}

		openIframeModal({ url: url.toString() });
		void Analytics.sdk.openIframeModal({
			game: this.game,
			sdk_adapter:
				FxIdDomainStoreEnumsSupportedWebPublishingPlatform[IframeTypeToSupportedPlatformMap[this.adapter]],
			url: url.toString()
		});

		return Promise.resolve({ Success: true });
	}
}
